import createClient, { type Client } from 'openapi-fetch'

import type { PageMeta } from './paging'
import type { paths } from './schema'

/** Field-keyed validation messages, exactly as the API envelope carries them. */
export type FieldErrors = Record<string, string[]>

/**
 * Every failed call arrives here, so a view never has to guess where a message
 * came from. `requestId` is the `X-Request-Id` the API stamps on each response;
 * it is the only handle support has for finding the matching server-side log,
 * so it is carried even when the body is unreadable.
 */
export class ApiError extends Error {
  readonly status: number
  readonly errors: FieldErrors
  readonly requestId: string | null

  constructor(status: number, message: string, errors: FieldErrors, requestId: string | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errors = errors
    this.requestId = requestId
  }
}

/** The machine code the API puts first in `errors.code` on a typed refusal. */
export function machineCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null
  return error.errors.code?.[0] ?? null
}

/** A typed refusal: the status, and the machine code the API put first in `errors.code`. */
export function isRefusal(error: unknown, status: number, code: string): boolean {
  return error instanceof ApiError && error.status === status && machineCode(error) === code
}

/** A destructive route refused because the five-minute step-up window has lapsed. */
export function isStepUpRequired(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 403 && machineCode(error) === 'step_up_required'
  )
}

/** Any write refused because the session is an operator's read-only view of a tenant. */
export function isReadOnlyToken(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 403 && machineCode(error) === 'read_only_token'
  )
}

/**
 * A call that never got an answer: the connection dropped, a blocker stepped
 * in, or the server was unreachable. Status 0 because no response exists; the
 * person is told to check their connection, and nothing is reported as a bug.
 */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0 && machineCode(error) === 'network'
}

function networkFailure(): ApiError {
  return new ApiError(0, 'network', { code: ['network'] }, null)
}

/**
 * `fetch` rejects with a bare `TypeError` when a request never reaches the
 * server. Every caller handles `ApiError`, so the rejection becomes one here;
 * an abort is the caller's own doing and passes through untouched.
 */
function reachable(fetchImpl: typeof globalThis.fetch): typeof globalThis.fetch {
  return async (input, init) => {
    try {
      return await fetchImpl(input, init)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw networkFailure()
    }
  }
}

/** A write refused because the tenant's subscription is read-only for non-payment (MRS 6.10). */
export function isTenantReadOnly(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 402 && machineCode(error) === 'tenant_read_only'
  )
}

/**
 * A body serialiser for the endpoints that take one file as multipart. The
 * default JSON serialiser is replaced for that call only, and the Content-Type
 * is left alone so the browser writes the multipart boundary itself.
 */
export function multipart(field: string): (body: Record<string, unknown>) => FormData {
  return (body) => {
    const form = new FormData()
    form.append(field, body[field] as File)
    return form
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function messageFrom(body: unknown, status: number): string {
  if (isRecord(body) && typeof body.message === 'string' && body.message !== '') {
    return body.message
  }
  return `Request failed with status ${status}.`
}

function fieldErrorsFrom(body: unknown): FieldErrors {
  if (!isRecord(body) || !isRecord(body.errors)) return {}
  const errors: FieldErrors = {}
  for (const [field, messages] of Object.entries(body.errors)) {
    if (Array.isArray(messages)) {
      errors[field] = messages.filter((entry): entry is string => typeof entry === 'string')
    }
  }
  return errors
}

/**
 * Narrows an openapi-fetch result to its payload, or throws.
 *
 * The API wraps every success in `{ success, data, message, errors }`, so views
 * want the inner `data`. Callers write `unwrap(await api.GET(...))` and handle a
 * single error type rather than branching on `result.error` everywhere.
 */
export function unwrap<T>(result: {
  data?: { data?: T } | undefined
  error?: unknown
  response: Response
}): T {
  const { data, error, response } = result

  // `data` is optional in the generated types because the envelope schema
  // marks it so; a 2xx without it is a contract violation, not a success.
  if (error !== undefined || !response.ok || data?.data === undefined) {
    throw new ApiError(
      response.status,
      messageFrom(error, response.status),
      fieldErrorsFrom(error),
      response.headers.get('X-Request-Id'),
    )
  }

  return data.data
}

/**
 * `unwrap` for the endpoints whose `data` is a plain list. The generated types
 * promise an array, but a stub, a proxy or a contract drift can hand back a
 * paged object instead, and a view that reads `.length` or `.find` on it
 * fails inside its render. The shape is checked once here, at the boundary,
 * so the caller gets the same `ApiError` it already handles and an empty or
 * failed state rather than a frozen page.
 */
export function unwrapList<T>(result: {
  data?: { data?: T[] } | undefined
  error?: unknown
  response: Response
}): T[] {
  const items = unwrap<T[]>(result)
  if (!Array.isArray(items)) throw malformed(result.response, 'malformed_list')
  return items
}

function isPageMeta(value: unknown): value is PageMeta {
  if (!isRecord(value)) return false
  return (['current_page', 'per_page', 'total', 'last_page'] as const).every(
    (key) => typeof value[key] === 'number',
  )
}

/**
 * `unwrap` for the lists whose page counters sit beside `data` rather than
 * inside it: console users, tags and device groups. Both halves are checked
 * at the boundary, because a panel that reads `meta.last_page` off a missing
 * object would fail inside its render rather than in its one failure path.
 */
export function unwrapPage<T>(result: {
  data?: { data?: T[]; meta?: PageMeta } | undefined
  error?: unknown
  response: Response
}): { items: T[]; meta: PageMeta } {
  const items = unwrap<T[]>(result)
  const meta = result.data?.meta
  if (!Array.isArray(items) || !isPageMeta(meta)) {
    throw malformed(result.response, 'malformed_page')
  }
  return { items, meta }
}

/**
 * The error for a 2xx whose payload is not the shape the contract promises:
 * a list where a record was due, a record missing the list it carries, or a
 * page without its counters. It is an `ApiError` so the view's one failure path shows it, with the
 * request id support needs to find the response.
 */
export function malformed(
  response: Response,
  code: 'malformed_list' | 'malformed_record' | 'malformed_page',
): ApiError {
  return new ApiError(
    response.status,
    'The API answered with a shape the console cannot read.',
    { code: [code] },
    response.headers.get('X-Request-Id'),
  )
}

/**
 * For calls whose success carries no `data`, such as a 202 or a bare envelope.
 * Throws the same `ApiError` that `unwrap` would, so views handle one type.
 */
export function ensureOk(result: { error?: unknown; response: Response }): void {
  const { error, response } = result
  if (error !== undefined || !response.ok) {
    throw new ApiError(
      response.status,
      messageFrom(error, response.status),
      fieldErrorsFrom(error),
      response.headers.get('X-Request-Id'),
    )
  }
}

export interface ApiClientOptions {
  baseUrl: string
  /** Read afresh on every request, so a sign-in mid-session is picked up. */
  getToken?: () => string | null
  /** Fired on any 401, so the session can be cleared in one place. */
  onUnauthorised?: () => void
  /** Fired on any 402: the API's one use of it is `tenant_read_only`. */
  onTenantReadOnly?: () => void
  /** Injection point for tests. */
  fetch?: typeof globalThis.fetch
}

export type ApiClient = Client<paths>

/** The shape `unwrap` reads, so an upload's answer is handled like any other call's. */
export interface RawResult<T> {
  data?: { data?: T }
  error?: unknown
  response: Response
}

export type Uploader = <T>(
  path: string,
  form: FormData,
  onProgress: (fraction: number) => void,
) => Promise<RawResult<T>>

function parsedBody(text: string): unknown {
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

function headersOf(xhr: XMLHttpRequest): Headers {
  const headers = new Headers()
  const requestId = xhr.getResponseHeader('X-Request-Id')
  if (requestId !== null) headers.set('X-Request-Id', requestId)
  return headers
}

/**
 * A multipart upload that reports how much of the body has gone. `fetch`
 * cannot say, so this one call goes through `XMLHttpRequest`, with the same
 * bearer token and the same 401 and 402 handling as the client. The answer is
 * shaped for `unwrap`, so a view sees the same `ApiError` either way.
 */
export function createUploader(options: ApiClientOptions): Uploader {
  return <T>(path: string, form: FormData, onProgress: (fraction: number) => void) =>
    new Promise<RawResult<T>>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${options.baseUrl}${path}`)
      const token = options.getToken?.() ?? null
      if (token !== null && token !== '') xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.setRequestHeader('Accept', 'application/json')
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total)
      })
      xhr.addEventListener('error', () => reject(networkFailure()))
      xhr.addEventListener('load', () => {
        if (xhr.status === 401) options.onUnauthorised?.()
        if (xhr.status === 402) options.onTenantReadOnly?.()
        const response = new Response(null, { status: xhr.status, headers: headersOf(xhr) })
        const body = parsedBody(xhr.responseText)
        resolve(response.ok ? { data: body as { data?: T }, response } : { error: body, response })
      })
      xhr.send(form)
    })
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch: reachable(options.fetch ?? ((input, init) => globalThis.fetch(input, init))),
  })

  client.use({
    onRequest({ request }) {
      const token = options.getToken?.() ?? null
      if (token !== null && token !== '') {
        request.headers.set('Authorization', `Bearer ${token}`)
      }
      request.headers.set('Accept', 'application/json')
      return request
    },
    onResponse({ response }) {
      if (response.status === 401) {
        options.onUnauthorised?.()
      }
      if (response.status === 402) {
        options.onTenantReadOnly?.()
      }
      return response
    },
  })

  return client
}
